import {
  type Invitation,
  type Member,
  type RoleRow,
  type TenantRole,
} from "@nexys/user-management-client";
import {
  AlertCircle,
  Mail,
  Plus,
  Trash2,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { useMemo, useRef, useState, type FormEvent } from "react";

import { useUmClient } from "../../context/auth-context.js";
import { useCall } from "../../hooks/use-call.js";
import { errorMessage } from "../../lib/error-message.js";
import { formatRelative, initialsOf } from "../../lib/utils.js";
import { EmptyState } from "../primitives/empty-state.js";
import { TenantRoleBadge } from "../roles/role-badge.js";
import { TenantRoleSelect } from "../roles/role-select.js";
import { Alert, AlertDescription } from "../ui/alert.js";
import { Avatar, AvatarFallback } from "../ui/avatar.js";
import { Badge } from "../ui/badge.js";
import { Button } from "../ui/button.js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu.js";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card.js";
import { Field } from "../ui/field.js";
import { Input } from "../ui/input.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select.js";
import { Skeleton } from "../ui/skeleton.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table.js";

export interface TenantMembersProps {
  organizationId: string;
  /** Allow current user to edit member roles and invites. */
  canManage?: boolean;
  /**
   * Pick existing users from a dropdown instead of typing an email.
   * Needs the system-wide user list, so superadmin/useradmin only.
   */
  userPicker?: boolean;
}

export const TenantMembers = ({
  organizationId,
  canManage = true,
  userPicker = false,
}: TenantMembersProps) => {
  const client = useUmClient();
  const { data, error, loading, refresh } = useCall("getFullTenant", { organizationId });
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const runAction = async <O, E>(
    key: string,
    op: () => Promise<{ ok: true; data: O } | { ok: false; error: E }>
  ) => {
    setBusy(key);
    setActionError(null);
    const res = await op();
    setBusy(null);
    if (res.ok) {
      await refresh();
    } else {
      setActionError(errorMessage(res.error as never, "Action failed."));
    }
  };

  const setRole = (m: Member, role: TenantRole) =>
    runAction(`role-${m.id}`, () =>
      client.updateMemberRole({ memberId: m.id, role, organizationId })
    );
  const remove = (m: Member) =>
    runAction(`remove-${m.id}`, () =>
      client.removeMember({ memberId: m.id, organizationId })
    );
  const assignRole = (m: Member, roleId: string) =>
    runAction(`assign-${m.id}`, () =>
      client.assignMemberRole({ orgId: organizationId, memberId: m.id, roleId })
    );
  const unassignRole = (m: Member, roleId: string) =>
    runAction(`unassign-${m.id}-${roleId}`, () =>
      client.removeMemberRole({ orgId: organizationId, memberId: m.id, roleId })
    );

  // Custom-role definitions are fetched lazily (first menu open) —
  // regular members never hit the admin-only roles endpoint.
  const [allRoles, setAllRoles] = useState<RoleRow[] | null>(null);
  const rolesRequested = useRef(false);
  const loadRoles = async () => {
    if (rolesRequested.current) return;
    rolesRequested.current = true;
    const res = await client.listTenantRoles({ orgId: organizationId });
    if (res.ok) setAllRoles(res.data.roles);
    else rolesRequested.current = false;
  };

  return (
    <Card>
      <CardHeader className="space-y-0">
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="size-4 text-muted-foreground" />
              Members
            </CardTitle>
            <CardDescription>
              {data
                ? `${data.members.length} member${data.members.length === 1 ? "" : "s"} in this tenant`
                : "Manage members and tenant-level roles."}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="grid gap-5">
        {error ? (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertDescription>{errorMessage(error)}</AlertDescription>
          </Alert>
        ) : null}
        {actionError ? (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertDescription>{actionError}</AlertDescription>
          </Alert>
        ) : null}

        {canManage && data ? (
          <>
            {userPicker ? (
              <PickUserRow
                organizationId={organizationId}
                memberUserIds={data.members.map((m) => m.userId)}
                onChanged={refresh}
              />
            ) : null}
            <AddOrInviteRow organizationId={organizationId} onChanged={refresh} />
          </>
        ) : null}

        <div className="overflow-hidden rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Member</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Custom roles</TableHead>
                <TableHead>Joined</TableHead>
                {canManage && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    <TableCell><Skeleton className="h-5 w-40" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-28" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                    {canManage && (
                      <TableCell className="text-right"><Skeleton className="ml-auto h-8 w-8 rounded" /></TableCell>
                    )}
                  </TableRow>
                ))
              ) : !data || data.members.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canManage ? 5 : 4} className="p-0">
                    <EmptyState
                      icon={Users}
                      title="No members yet"
                      description="Invite teammates to get started."
                      className="border-0 rounded-none bg-transparent"
                    />
                  </TableCell>
                </TableRow>
              ) : (
                data.members.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarFallback>
                            {initialsOf(m.user?.name, m.user?.email ?? m.userId)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="grid leading-tight">
                          <span className="text-sm font-medium">
                            {m.user?.name || m.user?.email || m.userId}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {m.user?.email}
                          </span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {canManage ? (
                        <TenantRoleSelect
                          value={m.role}
                          onChange={(next) => setRole(m, next)}
                          disabled={busy === `role-${m.id}`}
                        />
                      ) : (
                        <TenantRoleBadge role={m.role} />
                      )}
                    </TableCell>
                    <TableCell>
                      <MemberCustomRoles
                        member={m}
                        canManage={canManage}
                        allRoles={allRoles}
                        onOpenAssign={loadRoles}
                        onAssign={(roleId) => assignRole(m, roleId)}
                        onUnassign={(roleId) => unassignRole(m, roleId)}
                        busy={busy?.startsWith(`assign-${m.id}`) || busy?.startsWith(`unassign-${m.id}`) || false}
                      />
                    </TableCell>
                    <TableCell
                      title={m.createdAt}
                      className="text-sm text-muted-foreground"
                    >
                      {formatRelative(m.createdAt)}
                    </TableCell>
                    {canManage && (
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => remove(m)}
                          loading={busy === `remove-${m.id}`}
                          aria-label="Remove member"
                        >
                          <Trash2 className="text-destructive" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {data ? (
          <PendingInvitations invitations={data.invitations ?? []} />
        ) : null}
      </CardContent>
    </Card>
  );
};

/**
 * Badges for a member's assigned custom roles, with an assign menu and
 * per-badge remove when the viewer can manage the tenant.
 */
const MemberCustomRoles = ({
  member,
  canManage,
  allRoles,
  onOpenAssign,
  onAssign,
  onUnassign,
  busy,
}: {
  member: Member;
  canManage: boolean;
  allRoles: RoleRow[] | null;
  onOpenAssign: () => void;
  onAssign: (roleId: string) => void;
  onUnassign: (roleId: string) => void;
  busy: boolean;
}) => {
  const assigned = member.roles ?? [];
  const assignedIds = new Set(assigned.map((r) => r.id));
  const available = (allRoles ?? []).filter((r) => !assignedIds.has(r.id));

  return (
    <div className="flex flex-wrap items-center gap-1">
      {assigned.length === 0 && !canManage ? (
        <span className="text-sm text-muted-foreground">{"—"}</span>
      ) : (
        assigned.map((r) => (
          <Badge key={r.id} variant="secondary" className="gap-1 font-mono text-xs">
            {r.name}
            {canManage && (
              <button
                type="button"
                onClick={() => onUnassign(r.id)}
                disabled={busy}
                aria-label={`Remove role ${r.name}`}
                className="rounded-full opacity-60 transition-opacity hover:opacity-100 disabled:pointer-events-none"
              >
                <X className="size-3" />
              </button>
            )}
          </Badge>
        ))
      )}
      {canManage && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-6"
              onClick={onOpenAssign}
              disabled={busy}
              aria-label="Assign role"
            >
              <Plus className="size-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuLabel>Assign role</DropdownMenuLabel>
            {allRoles === null ? (
              <DropdownMenuItem disabled>Loading roles…</DropdownMenuItem>
            ) : available.length === 0 ? (
              <DropdownMenuItem disabled>
                {allRoles.length === 0
                  ? "No roles defined — create one in the Roles tab"
                  : "All roles assigned"}
              </DropdownMenuItem>
            ) : (
              available.map((r) => (
                <DropdownMenuItem key={r.id} onSelect={() => onAssign(r.id)}>
                  {r.name}
                </DropdownMenuItem>
              ))
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
};

const AddOrInviteRow = ({
  organizationId,
  onChanged,
}: {
  organizationId: string;
  onChanged: () => void;
}) => {
  const client = useUmClient();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<TenantRole>("member");
  const [busy, setBusy] = useState<"add" | "invite" | null>(null);
  const [error, setError] = useState<string | null>(null);

  // "Add" assigns an existing user directly; "Invite" emails a join
  // link for people without an account yet.
  const run = async (kind: "add" | "invite") => {
    setBusy(kind);
    setError(null);
    const input = { email: email.trim(), role, organizationId };
    const res =
      kind === "add"
        ? await client.addMember(input)
        : await client.inviteMember(input);
    setBusy(null);
    if (res.ok) {
      setEmail("");
      onChanged();
    } else {
      setError(
        errorMessage(
          res.error as never,
          kind === "add" ? "Couldn't add member." : "Couldn't send invitation."
        )
      );
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run("add");
  };

  return (
    <form
      onSubmit={submit}
      className="grid items-end gap-3 rounded-lg border border-dashed bg-muted/30 p-4 sm:grid-cols-[1fr_auto_auto_auto]"
    >
      <Field label="Invite someone" htmlFor="invite-email">
        <div className="relative">
          <Mail className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="invite-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="pl-9"
            placeholder="teammate@example.com"
          />
        </div>
      </Field>
      <Field label="Role" htmlFor="invite-role">
        <TenantRoleSelect value={role} onChange={setRole} />
      </Field>
      <Button type="submit" loading={busy === "add"} disabled={!email.trim() || busy !== null}>
        <UserPlus />
        Add member
      </Button>
      <Button
        type="button"
        variant="outline"
        loading={busy === "invite"}
        disabled={!email.trim() || busy !== null}
        onClick={() => void run("invite")}
      >
        <Mail />
        Send invite
      </Button>
      {error ? (
        <div className="sm:col-span-4">
          <Alert variant="destructive">
            <AlertCircle />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      ) : null}
    </form>
  );
};

/**
 * Superadmin variant of the add row: pick any existing user from a
 * dropdown and add them directly — no invitation round-trip.
 */
const PickUserRow = ({
  organizationId,
  memberUserIds,
  onChanged,
}: {
  organizationId: string;
  memberUserIds: string[];
  onChanged: () => void;
}) => {
  const client = useUmClient();
  const users = useCall("adminListUsers", { limit: 500 });
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState<TenantRole>("member");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const candidates = useMemo(() => {
    const members = new Set(memberUserIds);
    return (users.data?.rows ?? []).filter(
      (u) => !members.has(u.id) && u.type !== "service"
    );
  }, [users.data, memberUserIds]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!userId) return;
    setLoading(true);
    setError(null);
    const res = await client.addMember({ organizationId, userId, role });
    setLoading(false);
    if (res.ok) {
      setUserId("");
      setRole("member");
      onChanged();
    } else {
      setError(errorMessage(res.error, "Couldn't add member."));
    }
  };

  return (
    <form
      onSubmit={submit}
      className="grid items-end gap-3 rounded-lg border border-dashed bg-muted/30 p-4 sm:grid-cols-[1fr_auto_auto]"
    >
      <Field label="Add an existing user" htmlFor="pick-user">
        <Select value={userId} onValueChange={setUserId}>
          <SelectTrigger id="pick-user" className="h-9">
            <SelectValue
              placeholder={users.loading ? "Loading users…" : "Pick a user…"}
            />
          </SelectTrigger>
          <SelectContent>
            {candidates.map((u) => (
              <SelectItem key={u.id} value={u.id}>
                {u.name ? `${u.name} (${u.email})` : u.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Role" htmlFor="pick-user-role">
        <TenantRoleSelect value={role} onChange={setRole} />
      </Field>
      <Button type="submit" loading={loading} disabled={!userId || loading}>
        <UserPlus />
        Add member
      </Button>
      {error ? (
        <div className="sm:col-span-3">
          <Alert variant="destructive">
            <AlertCircle />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      ) : null}
    </form>
  );
};

const PendingInvitations = ({ invitations }: { invitations: Invitation[] }) => {
  const open = invitations.filter(
    (i) => i.status === "pending" || i.status === "expired"
  );
  if (open.length === 0) return null;
  return (
    <div className="grid gap-2">
      <p className="text-sm font-medium text-muted-foreground">
        Pending invitations
      </p>
      <div className="overflow-hidden rounded-lg border">
        <Table>
          <TableBody>
            {open.map((i) => (
              <TableRow key={i.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Mail className="size-4 text-muted-foreground" />
                    <span className="text-sm">{i.email}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <TenantRoleBadge role={i.role} />
                </TableCell>
                <TableCell className="text-right text-xs text-muted-foreground">
                  {i.status === "expired"
                    ? "Expired"
                    : i.expiresAt
                      ? `Expires ${formatRelative(i.expiresAt)}`
                      : "Pending"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
